import { useEffect, useState } from "react";
import {
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
  IconButton,
  Box,
  FormHelperText,
} from "@mui/material";
import { Remove } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { orderService } from "../../utils/services/orderService";
import { productService } from "../../utils/services/productService";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import buildQuery from "../../utils/query-builder";
import { useNavigate } from "react-router-dom";
import AddressLocationFields from "../../components/AddressLocation";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { padNumberWithZeros } from "../../utils/utility";
import { toast } from "react-toastify";
import { wmsService } from "../../utils/services/wmsProvider";
import { countryService } from "../../utils/services/country.service";
import { provinceService } from "../../utils/services/province.service";

const initialAddress = {
  first_name: "",
  last_name: "",
  address1: "",
  address2: "",
  company: "",
  city: "",
  zip: "",
  province: "",
  province_code: "",
  country: "",
  country_code: "",
  phone: "",
};

const fieldsToRender = {
  first_name: "",
  last_name: "",
  address1: "",
  address2: "",
  company: "",
  phone: "",
};

const requiredFields = [
  "first_name",
  "last_name",
  "address1",
  "city",
  "zip",
  "province",
  "country",
  "province_code",
  "country_code",
];

const initialOrderData = {
  order_id: "",
  shipping_method: "",
  warehouse: "",
  shipping_address: { ...initialAddress },
  billing_address: { ...initialAddress },
  customer: {
    name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: { ...initialAddress },
  },
  line_items: [
    {
      name: "",
      price: "",
      quantity: 1,
      sku: "",
      title: "",
      product_id: "",
      variant_id: "",
    },
  ],
};

const CreateOrder = () => {
  const [order, setOrder] = useState(initialOrderData);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const clientIdFromState = localStorage.getItem("selected_order_client_id");
  const orderType = localStorage.getItem("orderType") || "B2C";

  const handleFieldChange = (type, field, value) => {
    setOrder((prevOrder) => {
      const updatedOrder = { ...prevOrder };
      updatedOrder[`${type}_address`][field] = value;
      if (sameAsShipping && type === "shipping") {
        updatedOrder.billing_address[field] = value;
      }
      return updatedOrder;
    });
  };

  const handleCheckboxChange = () => {
    setSameAsShipping(!sameAsShipping);
    setOrder((prevOrder) => ({
      ...prevOrder,
      billing_address: !sameAsShipping
        ? { ...prevOrder.shipping_address }
        : { ...initialAddress },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!order.shipping_address[field]) {
        newErrors[`shipping_address_${field}`] = "This field is required";
      }
      if (!sameAsShipping && !order.billing_address[field]) {
        newErrors[`billing_address_${field}`] = "This field is required";
      }
    });

    if (!order.order_id) {
      newErrors.order_id = "Order ID is required";
    }

    order.line_items.forEach((item, index) => {
      if (!item.product) {
        newErrors[`product_${index}`] = "Product is required";
      }
      if (!item.variant) {
        newErrors[`variant_${index}`] = "Variant is required";
      }
    });
    if (orderType === "B2B" && !order.warehouse) {
      newErrors[`warehouse`] = "Warehouse is required";
    }

    // Phone validation if not blank
    if (
      order.shipping_address.phone &&
      !/^\+[1-9]\d{1,14}$/.test(order.shipping_address.phone)
    ) {
      newErrors.shipping_address_phone =
        "Phone number must be in the format +XXXXXXXXXXXXXX";
    }
    if (
      !sameAsShipping &&
      order.billing_address.phone &&
      !/^\+[1-9]\d{1,14}$/.test(order.billing_address.phone)
    ) {
      newErrors.billing_address_phone =
        "Phone number must be in the format +XXXXXXXXXXXXXX";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      let shipping = {
        ...order.shipping_address,
        name: `${order.shipping_address.first_name} ${order.shipping_address.last_name}`,
      };
      const orderData = {
        ...order,
        client_id: clientIdFromState,
        shipping_address: shipping,
        billing_address: sameAsShipping
          ? shipping
          : {
              ...order.billing_address,
              name: `${order.billing_address.first_name} ${order.billing_address.last_name}`,
            },
        customer: sameAsShipping
          ? shipping
          : {
              ...order.billing_address,
              name: `${order.billing_address.first_name} ${order.billing_address.last_name}`,
            },
        warehouse: order.warehouse?._id || null,
        line_items: order.line_items.map((item) => ({
          name: item.product?.name,
          price: item.variant.price,
          quantity: item.quantity,
          sku: item.variant.sku,
          variant_title: item.variant.title,
          title: item.product?.name,
          product_id: item.product?._id,
          variant_id: item.variant?._id,
        })),
        type: "M2C",
        sub_type: orderType,
        order_date: new Date().toISOString(),
      };
      try {
        await orderService.create(orderData);
        toast.success(`${orderType} order created successfully.`);
        resetAndBackToList();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const resetAndBackToList = () => {
    setOrder(initialOrderData);
    setSameAsShipping(false);
    setErrors({});
    navigate("/order-management?child=true");
    localStorage.removeItem("selected_order_client_id");
  };

  const updateOrder = (value, field) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      [field]: value,
    }));
  };

  const updateQuantity = (value, index) => {
    setOrder((prevOrder) => {
      const updatedItems = [...prevOrder.line_items];
      updatedItems[index].quantity = value;
      return { ...prevOrder, line_items: updatedItems };
    });
  };

  const handleProvinceSelect = (type, value) => {
    setOrder({
      ...order,
      [type]: {
        ...order[type],
        province: value?.name || "",
        province_code: value?.code || "",
        // country: value?.country?.name || "",
        // country_code: value?.country?.code2 || "",
      },
    });
  };

  const handleProvinceChange = (type, value) => {
    setOrder({
      ...order,
      [type]: {
        ...order[type],
        province: value?.name || "",
        province_code: value?.code || "",
      },
    });
  };

  const handleCountryChange = (type, value) => {
    setOrder({
      ...order,
      [type]: {
        ...order[type],
        province: "",
        province_code: "",
        country: value?.name || "",
        country_code: value?.code2 || "",
        city: "",
      },
    });
  };
  useEffect(() => {
    if (clientIdFromState) {
      fetchOrderPrefix();
    }
  }, [clientIdFromState]);
  let orderPrefix = "";
  const fetchOrderPrefix = async () => {
    try {
      const response = await clientConfigService.fetch(
        clientIdFromState,
        1,
        0,
        "order_id_prefix",
      );
      fetchOrderCount();

      if (response && response.total > 0) {
        orderPrefix = response.data[0].value || "";
      }
    } catch (error) {
      console.error("Error fetching order prefix:", error);
    }
  };

  const fetchOrderCount = async () => {
    try {
      const response = await orderService.fetchOrderCount(clientIdFromState);
      setOrder((prevOrder) => ({
        ...prevOrder,
        order_id: `${orderPrefix}${padNumberWithZeros(response.total + 1)}`,
      }));
    } catch (error) {
      console.error("Error fetching order count:", error);
    }
  };

  const onChangeWarehouse = async (warehouse) => {
    let country = await countryService.fetch(
      "",
      `$or[0][code2]=${warehouse.country}&$or[1][name]=${warehouse.country}`,
    );
    let province = await provinceService.fetch(
      "",
      `$or[0][code]=${warehouse.province}&$or[1][name]=${warehouse.province}`,
    );
    setOrder((prevOrder) => ({
      ...prevOrder,
      warehouse: warehouse,
      shipping_address: {
        ...prevOrder.shipping_address,
        country: country.data[0]?.name || "",
        country_code: country.data[0]?.code2 || "",
        first_name: warehouse.consignor_name.split(" ")[0] || "",
        last_name: warehouse.consignor_name.split(" ")[1] || "",
        address1: warehouse.address1 || "",
        address2: warehouse.address2 || "",
        city: warehouse.city || "",
        province: province.data[0]?.name || "",
        province_code: province.data[0]?.code || "",
        zip: warehouse.postal || "",
        phone: warehouse.contact_number || "",
      },
    }));
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        maxHeight: "98%",
        fontFamily: "'Poppins', sans-serif",
        padding: 5,
      }}
    >
      <Typography
        sx={{
          paddingBottom: 1,
          fontFamily: "'Poppins', sans-serif",
          textAlign: "left",
          marginBottom: 2,
        }}
        variant="h6"
        className="pb-4"
      >
        Create Order
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              marginBottom: "8px",
              textAlign: "left",
              fontSize: 14,
            }}
          >
            Order ID <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            size="small"
            required={true}
            fullWidth
            value={order.order_id}
            onChange={(e) => updateOrder(e.target.value, "order_id")}
            error={!!errors.order_id}
            helperText={errors.order_id}
            sx={{ fontFamily: "'Poppins', sans-serif" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              marginBottom: "8px",
              textAlign: "left",
              fontSize: 14,
            }}
          >
            Shipping Method
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={order.shipping_method}
            onChange={(e) => updateOrder(e.target.value, "shipping_method")}
            error={!!errors.shipping_method}
            helperText={errors.shipping_method}
            sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 14 }}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 2,
          borderWidth: 2,
          borderColor: "#D9D9D9",
          borderStyle: "solid",
          padding: "10px",
          borderRadius: 1,
          marginLeft: 0,
          width: "100%",
        }}
      >
        {order.line_items.map((item, index) => (
          <Grid container spacing={2} key={index} sx={{ marginBottom: 2 }}>
            {/* Product Selection */}
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  textAlign: "left",
                  marginBottom: "2px",
                  fontSize: 14,
                }}
              >
                Product <span style={{ color: "red" }}>*</span>
              </Typography>
              <AutocompleteWithPagination
                selectedClient={{ _id: clientIdFromState }}
                hideLabel
                selectedItem={item.product}
                onDataChange={(product) => {
                  const updatedItems = [...order.line_items];
                  updatedItems[index] = {
                    ...updatedItems[index],
                    product,
                    variant: null,
                    quantity: 1,
                  };
                  setOrder({ ...order, line_items: updatedItems });
                }}
                width="100%"
                optionKey="name"
                apiEndPoint={(limit, skip, search, client) => {
                  const query = buildQuery({ limit, skip });
                  return productService.fetchOmsProducts(client, search, query);
                }}
              />
              {errors[`product_${index}`] && (
                <FormHelperText error>
                  {errors[`product_${index}`]}
                </FormHelperText>
              )}
            </Grid>

            {/* Variant Selection */}
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  marginBottom: "8px",
                  textAlign: "left",
                  fontSize: 14,
                }}
              >
                Select Variant <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl
                size="small"
                fullWidth
                disabled={!item.product}
                error={!!errors[`variant_${index}`]}
                sx={{ fontFamily: "'Poppins', sans-serif" }}
              >
                <Select
                  value={item.variant?._id || ""}
                  onChange={(event) => {
                    const variant = item.product.variants.find(
                      (v) => v._id === event.target.value,
                    );
                    const updatedItems = [...order.line_items];
                    updatedItems[index] = { ...updatedItems[index], variant };
                    setOrder({ ...order, line_items: updatedItems });
                  }}
                >
                  {item.product?.variants?.map((variant) => (
                    <MenuItem
                      key={variant._id}
                      value={variant._id}
                      sx={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      {variant.sku}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors[`variant_${index}`] || " "}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Quantity and Action Buttons */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                {/* Quantity Field */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      marginBottom: "8px",
                      textAlign: "left",
                      fontSize: 14,
                    }}
                  >
                    Quantity <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    required={true}
                    disabled={!item.variant}
                    size="small"
                    value={item.quantity}
                    onChange={(e) => updateQuantity(e.target.value, index)}
                    type="number"
                    fullWidth
                    inputProps={{ min: 1 }}
                    error={!!errors[`quantity_${index}`]}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                  {errors[`quantity_${index}`] && (
                    <FormHelperText error>
                      {errors[`quantity_${index}`]}
                    </FormHelperText>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    paddingBottom: errors[`quantity_${index}`] ? "22px" : "0px",
                  }}
                >
                  <IconButton
                    disabled={order.line_items?.length === 1}
                    onClick={() => {
                      setOrder({
                        ...order,
                        line_items: order.line_items.filter(
                          (_, i) => i !== index,
                        ),
                      });
                    }}
                    size="small"
                    color="error"
                    sx={{
                      border: "1px solid var(--border-color)",
                      borderRadius: "4px",
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setOrder({
                        ...order,
                        line_items: [
                          ...order.line_items,
                          {
                            name: "",
                            price: "",
                            quantity: 1,
                            sku: "",
                            product: null,
                            variant: null,
                          },
                        ],
                      });
                    }}
                    size="small"
                    color="primary"
                    sx={{
                      border: "1px solid var(--border-color)",
                      borderRadius: "4px",
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        ))}
      </Grid>

      {orderType === "B2B" ? (
        <Grid item xs={12} md={12} mt={2}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              textAlign: "left",
              marginBottom: "2px",
              fontSize: 14,
            }}
          >
            Warehouse <span style={{ color: "red" }}>*</span>
          </Typography>
          <AutocompleteWithPagination
            selectedClient={{ _id: clientIdFromState }}
            hideLabel
            selectedItem={order.warehouse}
            onDataChange={(warehouse) => onChangeWarehouse(warehouse)}
            width="100%"
            optionKey={(item) => `${item.name} (${item.warehouse_code})`}
            apiEndPoint={(limit, skip, search, client) => {
              return wmsService.fetch(client, limit, skip);
            }}
          />
          {errors[`warehouse`] && (
            <FormHelperText error>{errors[`warehouse`]}</FormHelperText>
          )}
        </Grid>
      ) : (
        <></>
      )}

      <Typography
        mt={1}
        variant="h6"
        sx={{
          fontFamily: "'Poppins', sans-serif",
          textAlign: "left",
          fontSize: 16,
        }}
      >
        Shipping Address
      </Typography>
      <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
        {Object.keys(fieldsToRender).map((field) => (
          <Grid item xs={12} md={4} key={field}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                marginBottom: "8px",
                textAlign: "left",
                fontSize: 14,
              }}
            >
              {field
                .replace(/_/g, " ")
                .replace(/([A-Z])/g, " $1")
                .trim()
                .replace(/^\w/, (c) => c.toUpperCase())}
              {requiredFields.includes(field) && (
                <span style={{ color: "red" }}>*</span>
              )}
            </Typography>
            <TextField
              size="small"
              required={requiredFields.includes(field)}
              fullWidth
              value={
                order?.shipping_address[field]
                  ? order?.shipping_address[field]
                  : ""
              }
              onChange={(e) =>
                handleFieldChange("shipping", field, e.target.value)
              }
              error={!!errors[`shipping_address_${field}`]}
              helperText={errors[`shipping_address_${field}`]}
              sx={{ fontFamily: "'Poppins', sans-serif" }}
            />
          </Grid>
        ))}
        <AddressLocationFields
          type="shipping_address"
          onCountryChange={(country) => {
            handleCountryChange("shipping_address", country);
          }}
          onProvinceSelect={(province) => {
            handleProvinceSelect("shipping_address", province);
          }}
          onProvinceChange={(province) => {
            handleProvinceChange("shipping_address", province);
          }}
          address={order.shipping_address}
          errors={errors}
          onZipChange={(zip) => {
            handleFieldChange("shipping", "zip", zip);
            setErrors((prevErrors) => ({
              ...prevErrors,
              shipping_zip: "",
            }));
          }}
          onCityChange={(city) => {
            handleFieldChange("shipping", "city", city);
            setErrors((prevErrors) => ({
              ...prevErrors,
              shipping_city: "",
            }));
          }}
        />
      </Grid>

      <FormControlLabel
        sx={{ marginTop: 1, fontFamily: "'Poppins', sans-serif", fontSize: 14 }}
        control={
          <Checkbox checked={sameAsShipping} onChange={handleCheckboxChange} />
        }
        label="Billing Address is same as Shipping Address"
      />

      {!sameAsShipping && (
        <>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              textAlign: "left",
              fontSize: 16,
            }}
          >
            Billing Address
          </Typography>
          <Grid sx={{ marginTop: 1 }} container spacing={2}>
            {Object.keys(fieldsToRender).map((field) => (
              <Grid item xs={12} md={4} key={field}>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    marginBottom: "8px",
                    textAlign: "left",
                  }}
                >
                  {field
                    .replace(/_/g, " ")
                    .replace(/([A-Z])/g, " $1")
                    .trim()
                    .replace(/^\w/, (c) => c.toUpperCase())}
                  {requiredFields.includes(field) && (
                    <span style={{ color: "red" }}>*</span>
                  )}
                </Typography>
                <TextField
                  size="small"
                  required={requiredFields.includes(field)}
                  fullWidth
                  value={order.billing_address[field]}
                  onChange={(e) =>
                    handleFieldChange("billing", field, e.target.value)
                  }
                  error={!!errors[`billing_address_${field}`]}
                  helperText={errors[`billing_address_${field}`]}
                  sx={{ fontFamily: "'Poppins', sans-serif" }}
                />
              </Grid>
            ))}
            <AddressLocationFields
              type="billing_address"
              onCountryChange={(country) => {
                handleCountryChange("billing_address", country);
              }}
              onProvinceSelect={(province) => {
                handleProvinceSelect("billing_address", province);
              }}
              onProvinceChange={(province) => {
                handleProvinceChange("billing_address", province);
              }}
              address={order.billing_address}
              errors={errors}
              onZipChange={(zip) => {
                handleFieldChange("billing", "zip", zip);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  billing_zip: "",
                }));
              }}
              onCityChange={(city) => {
                handleFieldChange("billing", "city", city);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  billing_city: "",
                }));
              }}
            />
          </Grid>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "end" }}>
        {order.line_items.length > 0 && (
          <Typography
            variant="h6"
            sx={{
              marginTop: 2,
              fontWeight: "bold",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Order Total: $
            {Number(
              order.line_items.reduce(
                (total, item) =>
                  total +
                  (Number(item.variant?.price) * Number(item.quantity) || 0),
                0,
              ),
            )}
          </Typography>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={() => resetAndBackToList()}
          variant="outlined"
          sx={{
            marginTop: 2,
            marginRight: 1,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ marginTop: 2, fontFamily: "'Poppins', sans-serif" }}
        >
          Place Order
        </Button>
      </div>
    </div>
  );
};

export default CreateOrder;
